/**
 * Marks the part Smart Scroll is in with a `data-playing` attribute, which ViewSongPart outlines.
 *
 * Smart Scroll holds the page still through the parts already on screen, so without this a running
 * scroll looks no different from a stopped one. Written straight to the DOM rather than through
 * state: the parts are the elements the schedule measured, and React never sets this attribute, so
 * moving it from part to part costs no renders.
 *
 * Mounted in ViewBody, beside the schedule it reads.
 */

import { useEffect, useState } from 'react';

import { useGlobalContext } from '../context/GlobalContext';
import { getPlayingPart } from '../utils/autoScrollHelpers';
import { getSetting, settingKeys } from '../utils/settings';

// A part lasts seconds, so a few reads a second is plenty and keeps this off the animation frame
const POLL_MS = 250;

/** Fired by the settings modal so an open View picks the toggle up without a remount. */
export const PLAYING_PART_CHANGED_EVENT = 'highlightPlayingPartChanged';

export const isPlayingPartHighlightEnabled = () => Boolean(getSetting(settingKeys.highlightPlayingPart));

// The rendered part, by the same attributes the schedule measured it by. Continuous view can show
// one part id in two songs, so the song has to match too.
const findPart = ({ partId, songId }) =>
  Array.from(document.querySelectorAll('#view-body [data-part-id]')).find(
    (node) => node.dataset.partId === partId && node.dataset.songId === songId,
  ) || null;

const usePlayingPartHighlight = () => {
  const { isScrolling } = useGlobalContext();
  const [enabled, setEnabled] = useState(isPlayingPartHighlightEnabled);

  useEffect(() => {
    const syncEnabled = () => setEnabled(isPlayingPartHighlightEnabled());
    window.addEventListener(PLAYING_PART_CHANGED_EVENT, syncEnabled);
    return () => window.removeEventListener(PLAYING_PART_CHANGED_EVENT, syncEnabled);
  }, []);

  useEffect(() => {
    if (!enabled || !isScrolling) return undefined;

    let marked = null;

    const mark = () => {
      const part = getPlayingPart();
      const node = part ? findPart(part) : null;

      if (node === marked) return;
      if (marked) marked.removeAttribute('data-playing');
      if (node) node.setAttribute('data-playing', '');
      marked = node;
    };

    mark();
    const timer = setInterval(mark, POLL_MS);

    return () => {
      clearInterval(timer);
      if (marked) marked.removeAttribute('data-playing');
    };
  }, [enabled, isScrolling]);
};

export default usePlayingPartHighlight;
